/**
 * E03-F02-T11 — front-end model for the "Company Facts" block (pure, DOM-free).
 *
 * The facts are produced by the REAL «Search Company Website» action
 * (`POST /projects/:id/ai/company_website_facts`: free scrape of the client's own
 * site + Haiku extraction, never `web_search`) and persisted by the api to
 * `projects.data.company_facts` — the same per-project detail bag the Kickoff Data
 * panel reads. So the panel needs no query of its own: it renders whatever the
 * shared project-detail cache carries.
 *
 * Every read here is defensive (the bag is `Record<string, unknown>` and may come
 * from an older run with fewer fields) — a garbled value degrades to an empty row,
 * never a throw.
 */
import {
  companyWebsiteFactsSchema,
  companyFactsFailureCodeSchema,
  COMPANY_FACTS_FAILURE_MESSAGES,
  type CompanyWebsiteFacts,
} from '@eer/shared';

/** The labelled scalar rows, in display order (label ← the extraction field). */
const FACT_FIELDS: readonly (readonly [keyof CompanyWebsiteFacts, string])[] = [
  ['company_name', 'Company'],
  ['industry', 'Industry'],
  ['company_type', 'Type'],
  ['products', 'Products / services'],
  ['offices', 'Offices'],
  ['size', 'Size'],
  ['founded', 'Founded'],
];

/** One rendered row: a label and its value. */
export interface CompanyFactRow {
  readonly label: string;
  readonly value: string;
}

/**
 * Read `projects.data.company_facts` into the typed shape. Returns null when the
 * key is absent or unusable — the panel then renders nothing (the recruiter has
 * simply never pressed the button on this project).
 */
export function readCompanyFacts(
  data: Record<string, unknown> | null | undefined,
): CompanyWebsiteFacts | null {
  if (!data || typeof data !== 'object') return null;
  const raw = (data as { company_facts?: unknown }).company_facts;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const parsed = companyWebsiteFactsSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** The non-empty labelled rows (empty fields are dropped, like the legacy panels). */
export function buildCompanyFactRows(
  facts: CompanyWebsiteFacts | null | undefined,
): readonly CompanyFactRow[] {
  if (!facts) return [];
  const rows: CompanyFactRow[] = [];
  for (const [key, label] of FACT_FIELDS) {
    const value = String(facts[key] ?? '').trim();
    if (value) rows.push({ label, value });
  }
  return rows;
}

/** True when the collected facts carry nothing at all (panel stays hidden). */
export function isCompanyFactsEmpty(facts: CompanyWebsiteFacts | null | undefined): boolean {
  if (!facts) return true;
  return (
    buildCompanyFactRows(facts).length === 0 &&
    facts.summary.trim().length === 0 &&
    facts.facts.length === 0
  );
}

/** The "collected <date>" label next to the panel title ('' when unknown). */
export function collectedAtLabel(facts: CompanyWebsiteFacts | null | undefined): string {
  const raw = (facts?.collected_at ?? '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en');
}

/** Shown when nothing usable came back and no wording could be derived. */
export const COMPANY_FACTS_FALLBACK_MESSAGE = 'Company website lookup failed';

/**
 * Legacy lower-case reason tokens the route emitted BEFORE the coded negative
 * (`{ collected:false, code, message }`) landed. Kept only for a rolling deploy /
 * an old bundle: a token must never reach the recruiter as toast text.
 */
const LEGACY_CODE_ALIASES: Readonly<Record<string, string>> = {
  no_company_website: COMPANY_FACTS_FAILURE_MESSAGES.COMPANY_FACTS_NO_WEBSITE,
  site_unreachable: COMPANY_FACTS_FAILURE_MESSAGES.COMPANY_FACTS_SITE_UNREACHABLE,
  no_provider: COMPANY_FACTS_FAILURE_MESSAGES.COMPANY_FACTS_NO_PROVIDER,
  grounding_disabled: COMPANY_FACTS_FAILURE_MESSAGES.COMPANY_FACTS_DISABLED,
  project_not_found: COMPANY_FACTS_FAILURE_MESSAGES.COMPANY_FACTS_PROJECT_NOT_FOUND,
};

/** The human sentence for a KNOWN code (current or legacy), else undefined. */
function knownCodeMessage(code: string): string | undefined {
  const parsed = companyFactsFailureCodeSchema.safeParse(code);
  if (parsed.success) return COMPANY_FACTS_FAILURE_MESSAGES[parsed.data];
  return LEGACY_CODE_ALIASES[code];
}

/**
 * Human text for a failure CODE. The API now ships the wording itself
 * (`message`), so this is the fallback path: a legacy token, or a code from a
 * newer API than this bundle. An unknown code degrades to the generic notice —
 * never to a raw machine token, which is what the pre-fix toast showed.
 */
export function companyFactsErrorMessage(code: string): string {
  return knownCodeMessage(code) ?? COMPANY_FACTS_FALLBACK_MESSAGE;
}

/**
 * Toast text for a REAL transport failure (`ApiResultError.detail.message`).
 *
 * Unlike {@link companyFactsErrorMessage} an unrecognized string is KEPT: at this
 * layer it is a genuine human message ("Network error", "Unauthorized"), and
 * replacing it would hide the actual cause. The code lookup still runs first for
 * one reason — an OLD api answering the legacy `{ success:false, error:'<token>' }`
 * at HTTP 200 is demoted to an ApiResult error by the client, so the token would
 * otherwise surface verbatim in the toast.
 */
export function companyFactsTransportMessage(message: string | null | undefined): string {
  const text = (message ?? '').trim();
  if (!text) return COMPANY_FACTS_FALLBACK_MESSAGE;
  return knownCodeMessage(text) ?? text;
}

/**
 * The toast text for a negative `company_website_facts` body.
 *
 * Order: the API's own `message` → its `code` → the deprecated `error` alias →
 * the generic notice. Accepts a loose object so a legacy `{ success:false,
 * error:'site_unreachable' }` payload (old API, new bundle) still yields a
 * sentence rather than a token.
 */
export function companyFactsFailureMessage(res: unknown): string {
  if (!res || typeof res !== 'object') return COMPANY_FACTS_FALLBACK_MESSAGE;
  const r = res as Record<string, unknown>;
  if (typeof r.message === 'string' && r.message.trim().length > 0) return r.message;
  for (const key of ['code', 'error'] as const) {
    const v = r[key];
    if (typeof v === 'string' && v.trim().length > 0) return companyFactsErrorMessage(v.trim());
  }
  return COMPANY_FACTS_FALLBACK_MESSAGE;
}
